/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Router from 'next/router';
import useOrganizationCache from './useOrganizationIndexedDB';
import { useAuthStore } from '@/store/useAuthStore';
import { getOrgScopeModulePermission } from '@/hooks/usePermission';
import { getControllerFromRoute } from '@/helper/RouteHelper';
import { resolveOrgScope, normalizeModuleController } from '@/utils/orgScope';
import { useOrgScopeOptional } from '@/contexts/OrgScopeContext';
import { useRealtimeStore } from '@/store/RealtimeStore';
import { readUnitsFromIndexedDB } from '@/utils/organizationIndexedDBUtils';
import { isVanHoaPath } from '@/utils/vanHoaHuongDan';
import { organizationApi } from '@/services/organization/organization-api';

// Convert raw label to abbreviation (prefer parentheses, else initials)
// Hàm loại bỏ dấu tiếng Việt
const removeVietnameseTones = (str: string): string => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
};

const isEmployeeResigned = (entity: any): boolean => {
  const isEmployee = Boolean(entity?.TenNhanVien ?? entity?.tenNhanVien);
  if (!isEmployee) return false;
  return Boolean(entity?.NghiViec ?? entity?.nghiViec);
};

const formatEmployeeOrDepartmentName = (entity: any): string => {
  if (!entity || typeof entity !== 'object') return '';
  const name = String(
    entity.TenNhanVien ?? entity.tenNhanVien ?? entity.Ten ?? entity.ten ?? '',
  ).trim();
  if (!name) return '';
  return isEmployeeResigned(entity) ? `${name} (Nghỉ việc)` : name;
};

const resolveOrgFindName = (payload: any): string => {
  const entity = payload?.data ?? payload;
  return formatEmployeeOrDepartmentName(entity);
};

const toAbbrev = (raw: string): string => {
  const text = String(raw || '')
    .replace(/<[^>]*>/g, '')
    .trim();

  // Loại bỏ text trong ngoặc đơn trước khi xử lý
  const textWithoutParen = text.replace(/\([^)]*\)/g, '').trim();

  // Loại bỏ dấu tiếng Việt trước khi xử lý
  const textWithoutTones = removeVietnameseTones(textWithoutParen);
  const parts = textWithoutTones.split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '';

  // Nếu chỉ có 1 từ, lấy 2 chữ cái đầu
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  // Nếu có nhiều từ, lấy chữ cái đầu của 2 từ đầu tiên
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

interface UseLocalFilterProps {
  enabled?: boolean;
  profile?: any;
  /** Controller module; mặc định suy ra từ route hiện tại */
  controller?: string | null;
}

const employeeRecordKeys = (id: string): string[] => {
  const raw = String(id ?? '').trim();
  if (!raw) return [];
  const keys = [
    raw,
    raw.endsWith('_nv') ? raw : `${raw}_nv`,
    raw.replace(/_nv$/i, ''),
    /^nv_/i.test(raw) ? raw.replace(/^nv_/i, '') : `nv_${raw.replace(/_nv$/i, '')}`,
  ];
  return [...new Set(keys.filter(Boolean))];
};
const getRouterPathname = (): string =>
  Router?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '');

export const useLocalFilter = ({ enabled = true, profile: profileProp, controller: controllerProp }: UseLocalFilterProps = {}) => {
  const pathname = getRouterPathname();
  const authProfile = useAuthStore((s) => s.profile);
  const profile = profileProp ?? authProfile;
  const orgScopeCtx = useOrgScopeOptional();

  let resolvedController = controllerProp ?? orgScopeCtx?.controller ?? getControllerFromRoute(pathname);
  resolvedController = normalizeModuleController(resolvedController);


  const orgScope = useMemo(() => {
    if (!controllerProp && orgScopeCtx && orgScopeCtx.controller === resolvedController) {
      return orgScopeCtx.orgScope;
    }
    const orgModulePerm = getOrgScopeModulePermission(profile, resolvedController);
    return resolveOrgScope(profile, orgModulePerm, resolvedController);
  }, [controllerProp, orgScopeCtx, resolvedController, profile]);

  const orgScopeKey = useMemo(
    () =>
      JSON.stringify({
        controller: resolvedController,
        useFullOrg: orgScope.useFullOrg,
        treatAsQuanLy: orgScope.treatAsQuanLy,
        roleVuotCap: orgScope.roleVuotCap,
        roleNgangCap: orgScope.roleNgangCap,
        profileId: profile?.id,
      }),
    [resolvedController, orgScope, profile?.id]
  );
  const vhOrgScopeKey = useMemo(
    () =>
      JSON.stringify({
        useFullOrg: orgScope.useFullOrg,
        treatAsQuanLy: orgScope.treatAsQuanLy,
        roleVuotCap: orgScope.roleVuotCap,
        roleNgangCap: orgScope.roleNgangCap,
        profileId: profile?.id,
      }),
    [orgScope, profile?.id]
  );
  const [employees, setEmployees] = useState<any[]>([]);
  /** Toàn bộ NV/BP từ IndexedDB — dùng lookup hiển thị (loadEmployee, filterEmployeeLabel). */
  const [companyEmployees, setCompanyEmployees] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [companyDepartments, setCompanyDepartments] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  // const [perspectives, setPerspectives] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const invalidateUnits = useRealtimeStore((state) => state.invalidate.units);
  const hasLoadedOnceRef = useRef(false);
  const lastVhOrgScopeKeyRef = useRef('');
  const orgNameByIdRef = useRef<Record<string, string>>({});
  const orgNameInflightRef = useRef<Set<string>>(new Set());
  const [, setOrgNameCacheTick] = useState(0);

  const { clearAllCache, getCompanys, getSubOrganization } = useOrganizationCache();
  const isVanHoaRoute = isVanHoaPath(pathname);

  /** Load IndexedDB — VH: chỉ refresh nền khi scope quyền đổi, không reload mỗi controller. */
  useEffect(() => {
    if (!enabled) {
      setEmployees([]);
      setCompanyEmployees([]);
      setDepartments([]);
      setCompanyDepartments([]);
      setPositions([]);
      setUnits([]);
      setRoles([]);
      // setPerspectives([]);
      setLoading(false);
      setReady(false);
      hasLoadedOnceRef.current = false;
      lastVhOrgScopeKeyRef.current = '';
      return;
    }

    if (isVanHoaRoute && hasLoadedOnceRef.current && lastVhOrgScopeKeyRef.current === vhOrgScopeKey) {
      return;
    }

    let mounted = true;
    const backgroundRefresh = isVanHoaRoute && hasLoadedOnceRef.current;

    const loadData = async (profile?: any | null) => {
      if (!backgroundRefresh) {
        setLoading(true);
      }
      try {
        const company = await getCompanys();
        if (!mounted) return;

        const allDepartments = Array.isArray(company?.departments)
          ? company.departments
          : Object.values(company?.departments || {});
        const allEmployees = Array.isArray(company?.employees)
          ? company.employees
          : Object.values(company?.employees || {});

        setCompanyDepartments(allDepartments);
        setCompanyEmployees(allEmployees);

        if (profile) {
          if (orgScope.useFullOrg) {
            setDepartments(allDepartments);
            setEmployees(allEmployees);
          } else {
            const subOrganization = await getSubOrganization(
              profile,
              orgScope.roleVuotCap,
              orgScope.roleNgangCap,
              { treatAsQuanLy: orgScope.treatAsQuanLy }
            );
            if (!mounted) return;
            setDepartments(
              Array.isArray(subOrganization?.departments) ? subOrganization.departments : Object.values(subOrganization?.departments || {})
            );
            setEmployees(Array.isArray(subOrganization?.employees) ? subOrganization.employees : Object.values(subOrganization?.employees || {}));
          }
        } else {
          setDepartments(allDepartments);
          setEmployees(allEmployees);
        }
        setPositions(Array.isArray(company?.positions) ? company.positions : Object.values(company?.positions || {}));
        setUnits(Array.isArray(company?.units) ? company.units : Object.values(company?.units || {}));
        setRoles(Array.isArray(company?.roles) ? company.roles : Object.values(company?.roles || {}));
        // setPerspectives(Array.isArray(company?.perspectives) ? company.perspectives : Object.values(company?.perspectives || {}));
        setReady(true);
        hasLoadedOnceRef.current = true;
        if (isVanHoaRoute) {
          lastVhOrgScopeKeyRef.current = vhOrgScopeKey;
        }
      } catch (error) {
        if (mounted) {
          console.error('IndexedDB: Error loading data:', error);
          setEmployees([]);
          setCompanyEmployees([]);
          setDepartments([]);
          setCompanyDepartments([]);
          setPositions([]);
          setUnits([]);
          setRoles([]);
          // setPerspectives([]);
          setReady(false);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData(profile);
    return () => { mounted = false; };
  }, [enabled, getCompanys, profile, orgScopeKey, vhOrgScopeKey, getSubOrganization, isVanHoaRoute]);

  // Realtime unit sync: chỉ đọc IndexedDB, không gọi API organization
  useEffect(() => {
    if (!enabled || !invalidateUnits || !ready) return;

    console.log('[useLocalFilter][units] invalidateUnits → read IndexedDB');
    let mounted = true;
    (async () => {
      try {
        const nextUnits = await readUnitsFromIndexedDB();
        if (!mounted) return;
        console.log('[useLocalFilter][units] IndexedDB read', {
          count: nextUnits.length,
          labels: nextUnits.map((u: any) => u?.Ten ?? u?.ten),
        });
        setUnits(nextUnits);
      } catch (error) {
        console.error('[useLocalFilter][units] IndexedDB read failed:', error);
      }
    })();

    return () => { mounted = false; };
  }, [enabled, invalidateUnits, ready]);

  useEffect(() => {
    if (!enabled || !ready) return;

    const onIndexedDBReloaded = (event: Event) => {
      const module = (event as CustomEvent<{ module?: string }>).detail?.module;
      if (module !== 'rest' && module !== 'all') return;
      console.log('[useLocalFilter][units] indexedDBReloaded → read IndexedDB', { module });
      void readUnitsFromIndexedDB()
        .then((nextUnits) => {
          console.log('[useLocalFilter][units] IndexedDB read after reload', {
            count: nextUnits.length,
            labels: nextUnits.map((u: any) => u?.Ten ?? u?.ten),
          });
          setUnits(nextUnits);
        })
        .catch((error) => {
          console.error('[useLocalFilter][units] IndexedDB read failed after reload:', error);
        });
    };

    window.addEventListener('indexedDBReloaded', onIndexedDBReloaded);
    return () => window.removeEventListener('indexedDBReloaded', onIndexedDBReloaded);
  }, [enabled, ready]);

  /**
   * useOptimizedFilter: Bộ lọc tối ưu cho employee / department / position
   */
  const useOptimizedFilter = <T extends any>(data: T[] | Record<string, T>, keyField: keyof T = 'Key' as keyof T) => {
    const dataArray = useMemo(() => {
      const array = Array.isArray(data) ? data : typeof data === 'object' && data !== null ? Object.values(data) : [];
      return array.filter((item: any) => item != null);
    }, [data]);

    const dataMap = useMemo(() => {
      // IMPORTANT: Store multiple keys for the same item so lookups work whether callers use ID, Key, id, key, email...
      // This fixes cases where a record has both `Key` and `ID` and callers look up by `ID` (GUID) but map is keyed by `Key`.
      const m = new Map<string, any>();
      for (const item of dataArray as any[]) {
        if (!item) continue;
        const candidates: any[] = [
          item?.[keyField],
          item?.ID,
          item?.id,
          item?.Key,
          item?.key,
          item?.Email,
          item?.email,
          item?.EMAIL,
        ];
        for (const c of candidates) {
          const k = String(c ?? '').trim();
          if (!k) continue;
          if (!m.has(k)) m.set(k, item);
        }
      }
      return m;
    }, [dataArray, keyField]);

    const search = useCallback(
      (
        searchTerm: string,
        searchFields: string[] = ['Ten', 'TenNhanVien', 'Name', 'ten', 'tenNhanVien', 'name'],
        limit?: number
      ) => {
        const searchLower = searchTerm.toLowerCase();
        const filtered = dataArray.filter((item: any) =>
          searchFields.some((field) => {
            const value = item[field];
            return value && String(value).toLowerCase().includes(searchLower);
          })
        );
        return limit ? filtered.slice(0, limit) : filtered;
      },
      [dataArray]
    );

    const getById = useCallback(
      (id: string) => {
        const k = String(id ?? '').trim();
        if (!k) return null;
        return dataMap.get(k) || null;
      },
      [dataMap]
    );
    const filterByField = useCallback(
      (field: string, value: any) => dataArray.find((item: any) => item[field] === value) || null,
      [dataArray]
    );

    return { dataArray, dataMap, search, getById, filterByField };
  };

  /** Tạo filters cho từng loại dữ liệu */
  const employeeFilter = useOptimizedFilter(employees, 'Key');
  /** Lookup NV/BP toàn công ty (IndexedDB) — không phụ thuộc org scope quyền chọn/lọc. */
  const employeeLookupFilter = useOptimizedFilter(companyEmployees, 'Key');
  const departmentFilter = useOptimizedFilter(departments, 'Key');
  const departmentLookupFilter = useOptimizedFilter(companyDepartments, 'Key');
  const positionFilter = useOptimizedFilter(positions, 'Key');
  // const unitFilter = useOptimizedFilter(units, 'Key');
  const roleFilter = useOptimizedFilter(roles, 'Key');
  // const perspectiveFilter = useOptimizedFilter(perspectives, 'Key');

  const resolveEmployeeRecord = useCallback(
    (id: string): any | null => {
      if (!id?.trim() || !employeeLookupFilter.dataArray.length) return null;
      for (const key of employeeRecordKeys(id)) {
        const employee = employeeLookupFilter.getById(key);
        if (employee) return employee;
      }
      return null;
    },
    [employeeLookupFilter]
  );

  /**
   * Trả về label nhân viên hoặc bộ phận/chức vụ
   */
  const filterEmployeeLabel = useCallback(
    (id: string, key: string = '', email: string = '') => {
      if (!id && !email) return { value: '', label: '' };
      if (!employeeLookupFilter.dataArray.length) return { value: id, label: '' };

      let employee: any = null;
      let department: any = null;
      let position: any = null;

      if (email) {
        employee = employeeLookupFilter.filterByField('email', email);
      } else if (id.includes('@')) {
        employee = employeeLookupFilter.filterByField('Email', id);
      } else {
        employee = resolveEmployeeRecord(id);
      }

      if (!employee) return { value: id, label: '' };

      const deptId = employee?.BPID ?? employee?.bpid ?? '';
      const posId = employee?.CVID ?? employee?.cvid ?? '';
      if (deptId && departmentLookupFilter.dataArray.length) {
        department = departmentLookupFilter.getById(deptId);
      }
      if (posId && positionFilter.dataArray.length) {
        position = positionFilter.getById(posId);
      }

      const employeeEmail = employee?.email || employee?.tenTruyCap || '';
      const employeeName = employee?.TenNhanVien || employee?.tenNhanVien || '';
      const employeeMa = employee?.MaNhanVien || employee?.maNhanVien || '';
      const departmentName = department?.Ten || department?.ten || '';
      const positionName = position?.Ten || position?.ten || '';

      if (key === 'bophan') return { value: id, label: departmentName };
      if (key === 'chucvu') return { value: id, label: positionName };
      if (key === 'viettat-bophan') return { value: id, label: department?.TenVietTat || department?.tenVietTat || '' };
      if (key === 'viettat-chucvu') return { value: id, label: position?.TenVietTat || position?.tenVietTat || '' };

      let label = employeeName;
      if (employeeEmail) label += ` (${employeeEmail})`;
      if (employeeMa) label += ` (${employeeMa})`;
      if (departmentName) label += ` - ${departmentName}`;
      if (positionName) label += ` - ${positionName}`;
      return { value: id, label };
    },
    [employeeLookupFilter, departmentLookupFilter, positionFilter, resolveEmployeeRecord]
  );

  /** Viết tắt từ tên */
  const getAbbrevFromTitle = (title: string): string => {
    const parts = String(title || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  /** Lấy viết tắt bộ phận */
  const getDepartmentShortName = useCallback(
    (deptId: string) => {
      if (!deptId || !deptId.trim()) return '';
      if (!departmentLookupFilter.dataArray.length) return '';
      const dept: any = departmentLookupFilter.getById(deptId);
      return getAbbrevFromTitle(dept?.Ten || dept?.ten || '');
    },
    [departmentLookupFilter]
  );

  const getEmployeeOrDepartmentFullName = useCallback(
    (id: string | null | undefined) => {
      const raw = String(id ?? '').trim();
      if (!raw) return '';

      const cached = orgNameByIdRef.current[raw];
      if (cached) return cached;

      const fullName = getEmployeeOrDepartmentFullNameFallBack(raw);
      if (fullName) return fullName;

      if (!orgNameInflightRef.current.has(raw)) {
        orgNameInflightRef.current.add(raw);
        void organizationApi
          .find(raw)
          .then((res: any) => {
            const name = resolveOrgFindName(res);
            orgNameByIdRef.current[raw] = name;
            if (name) setOrgNameCacheTick((n) => n + 1);
          })
          .catch(() => {
            orgNameByIdRef.current[raw] = '';
          })
          .finally(() => {
            orgNameInflightRef.current.delete(raw);
          });
      }

      return '';
    },
    [employeeLookupFilter, departmentLookupFilter, resolveEmployeeRecord]
  );

  /** Lấy full name theo id (employee hoặc department) */
  const getEmployeeOrDepartmentFullNameFallBack = useCallback(
    (id: string | null | undefined) => {
      const raw = String(id ?? '');
      if (!raw) return '';

      console.log('getEmployeeOrDepartmentFullName ==> raw = ', raw, /^emp_/i.test(raw));
      
      // 1) Nếu là employee node: emp_{empid}_dep_{bpid}
      if (/^emp_/i.test(raw)) {
        console.log('getEmployeeOrDepartmentFullName ==> raw starts with emp = ', raw);
        const parsed = raw.match(/^emp_(.+)_dep_(.+)$/i);
        if (parsed) {
          const empId = parsed[1];

          const emp =
            resolveEmployeeRecord(empId) ||
            employeeLookupFilter.getById(empId) ||
            employeeLookupFilter.getById(empId.replace(/_nv$/i, '')) ||
            employeeLookupFilter.getById(`${empId}_nv`);

          if (emp) return formatEmployeeOrDepartmentName(emp);
        }
        return ''; // không parse được thì thôi
      }
      else if (raw.endsWith('_nv')) {
        console.log('getEmployeeOrDepartmentFullName ==> raw ends with _nv = ', raw);
        const nv = resolveEmployeeRecord(raw) || employeeLookupFilter.getById(raw);
        return formatEmployeeOrDepartmentName(nv);
      }

      // Id trần trùng đúng mã bộ phận thì là bộ phận: resolveEmployeeRecord tự thêm hậu tố _nv
      // nên `demo_2974` (bộ phận) sẽ khớp nhầm nhân viên `demo_2974_nv`.
      const dept = departmentLookupFilter.dataArray.length ? departmentLookupFilter.getById(raw) : null;
      if (dept) return dept?.Ten || dept?.ten || '';

      const employee = resolveEmployeeRecord(raw);
      if (employee) return formatEmployeeOrDepartmentName(employee);

      return '';
    },
    [employeeLookupFilter, departmentLookupFilter, resolveEmployeeRecord]
  );

  /** Lấy viết tắt nhân viên hoặc bộ phận */
  const getEmployeeOrDepartmentAbbrev = useCallback(
    (id: string): { abbr: string; useToAbbrev: boolean } => {
      if (!id?.trim()) return { abbr: '', useToAbbrev: false };

      const getAbbrev = (prefix?: string, fullName?: string) => {
        if (prefix?.trim()) return prefix.trim();
        return toAbbrev(fullName || '');
      };

      const isEmployee = id.includes('nv');

      if (isEmployee) {
        if (!employeeLookupFilter.dataArray.length) return { abbr: '', useToAbbrev: false };
        const employee = resolveEmployeeRecord(id) || employeeLookupFilter.getById(id);
        return { abbr: getAbbrev(employee?.Prefix, employee?.TenNhanVien), useToAbbrev: false };
      }

      if (departmentLookupFilter.dataArray.length) {
        const department = departmentLookupFilter.getById(id);

        if (department) {
          return { abbr: getAbbrev(department?.Prefix, department?.Ten), useToAbbrev: false };
        }
      }

      if (employeeLookupFilter.dataArray.length) {
        const employee = resolveEmployeeRecord(id) || employeeLookupFilter.getById(id);
        return { abbr: getAbbrev(employee?.Prefix, employee?.TenNhanVien), useToAbbrev: false };
      }

      return { abbr: '', useToAbbrev: false };
    },
    [employeeLookupFilter, departmentLookupFilter, resolveEmployeeRecord]
  );

  /** Lấy BPID theo nv_[id] */
  const getDepartmentIdByNvId = useCallback(
    (nvId: string): string => {
      if (!nvId || !nvId.trim()) return '';
      const key = nvId.startsWith('nv_') ? nvId : `nv_${nvId}`;
      const rawKey = nvId.replace(/^nv_/, '');
      const employee = resolveEmployeeRecord(nvId) || employeeLookupFilter.getById(key) || employeeLookupFilter.getById(rawKey);
      const deptId = employee?.BPID ?? employee?.bpid ?? '';
      return typeof deptId === 'string' ? deptId : '';
    },
    [employeeLookupFilter, resolveEmployeeRecord]
  );
  const buildEmpDepKeyFromNvId = useCallback(
    (nvId: string): string => {
      if (!nvId || !nvId.trim()) return '';
      const trimmed = nvId.startsWith('nv_') ? nvId.substring(3) : nvId;
      const deptId = getDepartmentIdByNvId(nvId);
      if (!trimmed) return '';
      return `emp_${trimmed}_dep_${deptId || ''}`;
    },
    [getDepartmentIdByNvId]
  );

  /** Lấy label hiển thị từ emp_[nvid]_dep_[bpid] */
  const getFullNameByEmpDepKey = useCallback(
    (empDepKey: string): string => {
      if (!empDepKey) return '';
      // Match format: emp_<any_chars>_dep_<depId> (support UUID and complex IDs)
      const match = empDepKey.match(/^emp_(.+?)_dep_(.+)$/);
      if (!match) return getEmployeeOrDepartmentFullName(empDepKey);
      const nvKey = `nv_${match[1]}`;
      const rawKey = match[1];
      const deptId = match[2];
      const employee =
        resolveEmployeeRecord(nvKey) ||
        resolveEmployeeRecord(rawKey) ||
        employeeLookupFilter.getById(nvKey) ||
        employeeLookupFilter.getById(rawKey);
      const department = departmentLookupFilter.getById(deptId);
      const employeeName = employee?.TenNhanVien || employee?.tenNhanVien || '';
      const departmentName = department?.Ten || department?.ten || '';
      return departmentName ? `${employeeName} - ${departmentName}` : employeeName;
    },
    [employeeLookupFilter, departmentLookupFilter, getEmployeeOrDepartmentFullName, resolveEmployeeRecord]
  );

  //========== Load data from IndexedDB ==========
  /** Lấy thông tin theo bộ phận */
  const loadByDepartment = useCallback(
    (deptId: string) => ({
      shortName: getDepartmentShortName(deptId),
    }),
    [getDepartmentShortName]
  );

  /** Load department theo id */
  const loadDepartment = useCallback(
    (id: string) => {
      if (!id || !id.trim()) return null;
      if (!departmentLookupFilter.dataArray.length) return null;
      return departmentLookupFilter.getById(id);
    },
    [departmentLookupFilter]
  );

  /** Load department data - lấy giá trị field cụ thể */
  const loadDepartmentData = useCallback(
    (id: string, field: string) => {
      const dept = loadDepartment(id);
      if (!dept) return null;
      // Hỗ trợ cả camelCase và PascalCase
      const value = dept[field] ?? dept[field.charAt(0).toUpperCase() + field.slice(1)];
      return value ?? null;
    },
    [loadDepartment]
  );

  /** Load position theo id */
  const loadPosition = useCallback(
    (id: string) => {
      if (!id || !id.trim()) return null;
      if (!positionFilter.dataArray.length) return null;
      return positionFilter.getById(id);
    },
    [positionFilter]
  );

  /** Load position data - lấy giá trị field cụ thể */
  const loadPositionData = useCallback(
    (id: string, field: string) => {
      const position = loadPosition(id);
      if (!position) return null;
      // Hỗ trợ cả camelCase và PascalCase
      const value = position[field] ?? position[field.charAt(0).toUpperCase() + field.slice(1)];
      return value ?? null;
    },
    [loadPosition]
  );

  /** Load role theo id */
  const loadRole = useCallback(
    (id: string) => {
      if (!id || !id.trim()) return null;
      if (!roleFilter.dataArray.length) return null;
      return roleFilter.getById(id);
    },
    [roleFilter]
  );

  /** Load role data - lấy giá trị field cụ thể hoặc toàn bộ object */
  const loadRoleData = useCallback(
    (id: string, field?: string) => {
      const role = loadRole(id);
      if (!role) return null;
      // Nếu không có field, trả về toàn bộ object
      if (!field) return role;
      // Hỗ trợ cả camelCase và PascalCase
      const value = role[field] ?? role[field.charAt(0).toUpperCase() + field.slice(1)];
      return value ?? null;
    },
    [loadRole]
  );

  /** Load employee theo id — tra cứu toàn công ty từ IndexedDB. */
  const loadEmployee = useCallback(
    (id: string) => resolveEmployeeRecord(id),
    [resolveEmployeeRecord]
  );

  /** Load employee data - lấy giá trị field cụ thể */
  const loadEmployeeData = useCallback(
    (id: string, field: string) => {
      const employee = loadEmployee(id);
      if (!employee) return null;
      // Hỗ trợ cả camelCase và PascalCase
      const value = employee[field] ?? employee[field.charAt(0).toUpperCase() + field.slice(1)];
      return value ?? null;
    },
    [loadEmployee]
  );

  /** Lấy cả department và position (thường dùng với employee) */
  const getDepartmentAndPosition = useCallback(
    (employeeId?: string, departmentId?: string, positionId?: string) => {
      let dept: any = null;
      let pos: any = null;

      // Nếu có employeeId, lấy department và position từ employee
      if (employeeId) {
        const employee = loadEmployee(employeeId);
        if (employee) {
          const deptIdFromEmp = employee?.BPID ?? employee?.bpid ?? '';
          const posIdFromEmp = employee?.CVID ?? employee?.cvid ?? '';
          if (deptIdFromEmp) dept = loadDepartment(deptIdFromEmp);
          if (posIdFromEmp) pos = loadPosition(posIdFromEmp);
        }
      }

      // Nếu có departmentId trực tiếp
      if (!dept && departmentId) {
        dept = loadDepartment(departmentId);
      }

      // Nếu có positionId trực tiếp
      if (!pos && positionId) {
        pos = loadPosition(positionId);
      }

      return { department: dept, position: pos };
    },
    [loadEmployee, loadDepartment, loadPosition]
  );

  //========== End Load data from IndexedDB ==========

  /** Xóa dữ liệu */
  const clearData = useCallback(() => {
    setEmployees([]);
    setCompanyEmployees([]);
    setDepartments([]);
    setCompanyDepartments([]);
    setPositions([]);
    setUnits([]);
    setRoles([]);
    setLoading(false);
    setReady(false);
  }, []);

  /** Xóa cache IndexedDB */
  const clearCache = useCallback(async () => {
    try {
      await clearAllCache();
      clearData();
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }, [clearAllCache, clearData]);

  /** Gộp label chọn nhiều */
  const buildSelectionLabels = useCallback(
    (
      idsCurrent: string[] = [],
      idsRemoved: string[] = [],
      totals: { departmentIds: string[]; employeeIds: string[] }
    ) => {
      const norm = (v: any): string => String(v ?? '').trim();
      if (!totals || (!totals.departmentIds?.length && !totals.employeeIds?.length)) {
        return {
          labels: [],
          isDeptAll: false,
          isEmpAll: false,
          caseType: 'partial' as const,
          effectiveSelectedIds: [],
        };
      }

      if (!employeeFilter.dataArray.length || !departmentFilter.dataArray.length) {
        return {
          labels: [],
          isDeptAll: false,
          isEmpAll: false,
          caseType: 'partial' as const,
          effectiveSelectedIds: [],
        };
      }
      const effective = (idsCurrent || [])
        .map(norm)
        .filter(Boolean)
        .filter((id) => id);
      const departmentIdSet = new Set((totals?.departmentIds || []).map(norm).filter(Boolean));
      const employeeIdSet = new Set((totals?.employeeIds || []).map(norm).filter(Boolean));
      let deptSelected = 0;
      let empSelected = 0;
      for (const id of effective) {
        if (employeeIdSet.has(id) || id.includes('nv')) empSelected++;
        else if (departmentIdSet.has(id)) deptSelected++;
        else {
          const maybeDept = departmentFilter.getById(id);
          const maybeEmp = employeeFilter.getById(id);
          if (maybeEmp) empSelected++;
          else if (maybeDept) deptSelected++;
        }
      }
      const deptTotal = departmentIdSet.size;
      const empTotal = employeeIdSet.size;
      const labels: string[] = [];
      const isDeptAll = deptTotal > 0 && deptSelected >= deptTotal;
      const isEmpAll = empTotal > 0 && empSelected >= empTotal;
      if (isDeptAll) labels.push('All bộ phận');
      if (isEmpAll) labels.push('All nhân viên');

      let caseType: 'dept_all' | 'emp_all' | 'both_all' | 'partial' = 'partial';
      if (isDeptAll && isEmpAll) caseType = 'both_all';
      else if (isDeptAll) caseType = 'dept_all';
      else if (isEmpAll) caseType = 'emp_all';
      return { labels, isDeptAll, isEmpAll, caseType, effectiveSelectedIds: effective };
    },
    [employeeFilter, departmentFilter]
  );

  /** Convert units thành option select */
  const transformUnitsToOptions = useCallback(() => {
    if (!units) return [];
    const asArray: any[] = Array.isArray(units) ? units : typeof units === 'object' ? Object.values(units) : [];
    const options: any[] = [];
    asArray.forEach((unit: any) => {
      if (!unit) return;
      const id = unit.ID ?? unit.id;
      const name = unit.Ten ?? unit.ten;
      if (id && name) options.push({ label: name, value: id });
      const childs = unit.Childs ?? unit.childs ?? [];
      if (Array.isArray(childs)) {
        childs.forEach((child: any) => {
          if (!child) return;
          const cid = child.ID ?? child.id;
          const cname = child.Ten ?? child.ten;
          if (cid && cname) options.push({ label: cname, value: cid });
        });
      }
    });
    return options;
  }, [units]);

  return {
    ready,
    employees,
    departments,
    positions,
    units,
    roles,
    loading,
    employeeFilter,
    departmentFilter,
    positionFilter,
    roleFilter,
    transformUnitsToOptions,
    filterEmployeeLabel,
    getDepartmentShortName,
    getEmployeeOrDepartmentFullName,
    getEmployeeOrDepartmentAbbrev,
    loadByDepartment,
    clearData,
    clearCache,
    buildSelectionLabels,
    getDepartmentIdByNvId,
    buildEmpDepKeyFromNvId,
    getFullNameByEmpDepKey,
    loadDepartment,
    loadDepartmentData,
    loadPosition,
    loadPositionData,
    loadEmployee,
    loadEmployeeData,
    loadRole,
    loadRoleData,
    getDepartmentAndPosition,
  };
};

